import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
    Alert,
    Badge,
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    Row,
} from 'reactstrap';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheckCircle,
    faPlus,
    faShieldAlt,
    faSpinner,
    faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { SessionContext } from '../../shared/session';
import Requester from '../../shared/Requester';
import Header from '../../shared/Header';
import FormExpediente from './FormExpediente';
import FormSociedad from './FormSociedad';
import NodoEntidad from './NodoEntidad';
import ArbolPLD from './ArbolPLD';
import { CATALOGO_OPERACIONES } from '../catalogos/catalogoOperaciones';
import { evaluarOperacionPLD } from '../motor/motorPLD';
import {
    createNodo,
    createOperacion,
    listOperacionSujetos,
    listPaises,
    updateNodo,
    updateOperacion,
    updateSujeto,
    upsertSujetoByRfc,
} from '../api/pldApi';
import {
    buildSeedFromProceso,
    mapProcesoMediacionTipoToTipoOperacionPld,
} from '../utils/procesoSeedToPld';
import FormExpedienteInmuebles from './FormExpedienteInmuebles';

const initialSolicitante = {
    nacionalidad: 'México',
    nombre: '',
    paterno: '',
    materno: '',
    rfc: '',
    comparece: '',
    caracter: '',
    otroEspecificacion: '',
};

function nuevoSocio() {
    return {
        id: String(Date.now()) + Math.random().toString(16).slice(2, 6),
        rolBase: '',
        tipo: 'Fisica',
        nacionalidad: 'México',
        nombre: '',
        rfc: '',
        acciones: '',
    };
}

// Aplana el árbol de participantes en una lista plana para el motor PLD,
// preservando id_nodo (id de la entidad), rol forzado por la jerarquía y
// el porcentaje de acciones calculado en el nivel raíz.
function aplanarParticipantes(sociosRaiz, totalAcciones, entidadPLDExtra) {
    const planos = [];
    const total = parseFloat(totalAcciones || '0');

    function recorrer(entidad, rolForzado, esHistorico, esRaiz, porcentajeDirecto) {
        if (!entidad) return;
        const rolFinal = rolForzado || entidad.rolBase || '';
        let porcentaje;
        if (esRaiz && total > 0 && entidad.acciones) {
            porcentaje = (parseFloat(entidad.acciones) / total) * 100;
        } else if (porcentajeDirecto !== undefined) {
            // subSocios de Moral: su campo acciones ya representa % directo
            porcentaje = porcentajeDirecto;
        }
        planos.push({
            id_nodo: entidad.id,
            sujeto_id: entidad.id,
            rol: rolFinal,
            porcentaje_acciones: porcentaje,
            es_historico: !!esHistorico,
            tipo: entidad.tipo,
        });

        if (entidad.tieneRepresentante === 'si' && entidad.representante) {
            recorrer(entidad.representante, 'APODERADO', false, false);
        } else if (entidad.tipo === 'Moral' && entidad.representante) {
            recorrer(entidad.representante, 'REPRESENTANTE LEGAL', false, false);
        }

        if (entidad.tieneSociosMayores === 'no' && entidad.adminUnico) {
            recorrer(
                entidad.adminUnico,
                'ADMINISTRADOR POR CONTROL (BC)',
                false,
                false
            );
        } else if (
            entidad.tieneSociosMayores === 'si' &&
            Array.isArray(entidad.subSocios)
        ) {
            entidad.subSocios.forEach((sub) =>
                recorrer(
                    sub,
                    'DUEÑO BENEFICIARIO (CASCADA)',
                    false,
                    false,
                    parseFloat(sub.acciones) || undefined
                )
            );
        }
    }

    sociosRaiz.forEach((s) => recorrer(s, null, false, true));
    if (entidadPLDExtra && entidadPLDExtra.rfc) {
        const esHist = (entidadPLDExtra.rolBase || '').includes('HISTÓRICO');
        recorrer(entidadPLDExtra, null, esHist, false);
    }
    return planos;
}

function participantesRaizSinRfc(socios) {
    const faltan = [];
    for (const s of socios || []) {
        const nombre = (s.nombre || '').trim();
        const rfc = (s.rfc || '').trim();
        if (nombre && !rfc) {
            faltan.push(s);
        }
    }
    return faltan;
}

export default function NuevoExpediente({ unidadId: unidadIdProp }) {
    const { unidad: unidadRoute, operacionId: operacionIdEdicion } = useParams();
    const esEdicion = Boolean(operacionIdEdicion);
    const location = useLocation();
    const history = useHistory();
    const session = useContext(SessionContext);

    const unidadId =
        unidadIdProp ||
        unidadRoute ||
        (session.unidad && session.unidad._id) ||
        null;

    const esInmobiliaria = session.unidad?.actividad === 'inmobiliaria'

    const [asunto, setAsunto] = useState('');
    const [tipoOperacion, setTipoOperacion] = useState(esInmobiliaria ? 'INMOBILIARIO' : 'CONSTITUTIVA');
    const [solicitante, setSolicitante] = useState(initialSolicitante);
    const [denominacion, setDenominacion] = useState(esInmobiliaria ? 'INMOBILIARIA' : "");
    const [capFijo, setCapFijo] = useState('');
    const [capVariable, setCapVariable] = useState('');
    const [totalAcciones, setTotalAcciones] = useState('');
    const [sociosRaiz, setSociosRaiz] = useState([]);
    const [paises, setPaises] = useState([]);

    const [pldOtroSocio, setPldOtroSocio] = useState('');
    const [pldFirmante, setPldFirmante] = useState('');
    const [entidadPLDExtra, setEntidadPLDExtra] = useState(null);
    const [verAdminConst, setVerAdminConst] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [procesoIdVinculo, setProcesoIdVinculo] = useState(null);
    const [seedDesdeProcesoError, setSeedDesdeProcesoError] = useState(null);
    const [mostrarAcciones, setMostrarAcciones] = useState(true);
    const [esConstitutiva, setEsConstitutiva] = useState(true);
    const [tipoRepresentacion, setTipoRepresentacion] = useState('acciones');
    // Participantes ya guardados en la operación (en modo edición).
    // Cada elemento es compatible con NodoEntidad + lleva id_sujeto e id_relacion.
    const [sujetosExistentes, setSujetosExistentes] = useState([]);

    useEffect(() => {
        if (!unidadId) return undefined;
        let cancelled = false;
        async function cargar() {
            try {
                const data = await listPaises(unidadId);
                if (!cancelled && Array.isArray(data)) setPaises(data);
            } catch {
                if (!cancelled) setPaises([]);
            }
        }
        cargar();
        return () => {
            cancelled = true;
        };
    }, [unidadId]);

    useEffect(() => {
        const params = new URLSearchParams(location.search || '');
        const pid = params.get('proceso_id');
        if (!pid || !unidadId) {
            setProcesoIdVinculo(null);
            setSeedDesdeProcesoError(null);
            return undefined;
        }
        setProcesoIdVinculo(pid);
        let cancelled = false;
        setSeedDesdeProcesoError(null);
        (async () => {
            try {
                const [cliRes, ipRes, firRes] = await Promise.all([
                    Requester.get('clientes?onlyActive=true'),
                    Requester.get(
                        `proceso/${unidadId}/infoPreliminar/${pid}`
                    ),
                    Requester.get(`proceso/${unidadId}/${pid}/firmantes`),
                ]);
                if (cancelled) return;
                const clientes = cliRes.data || [];
                const raw = ipRes.data && ipRes.data.infoPreliminar;
                if (!raw) {
                    setSeedDesdeProcesoError(
                        'No se encontró información preliminar del proceso.'
                    );
                    return;
                }
                const clienteId = raw.cliente;
                const clienteResuelto =
                    clientes.find((c) => c._id === clienteId) || null;
                const firmantes =
                    (firRes.data && firRes.data.firmantes) || [];
                const tipoPld = mapProcesoMediacionTipoToTipoOperacionPld(
                    raw.tipo
                );
                const seed = buildSeedFromProceso({
                    infoPreliminarDoc: raw,
                    clienteResuelto,
                    firmantes,
                    tipoOperacionPld: tipoPld,
                });
                setAsunto(seed.asunto);
                setTipoOperacion(seed.tipoOperacion);
                setDenominacion(seed.denominacion);
                if (seed.solicitanteInitial) {
                    setSolicitante((prev) => ({
                        ...prev,
                        ...seed.solicitanteInitial,
                    }));
                }
                setSociosRaiz(seed.sociosRaizInitial);
            } catch (err) {
                if (!cancelled) {
                    const msg =
                        (err.response &&
                            err.response.data &&
                            err.response.data.error) ||
                        err.message ||
                        'No se pudo cargar datos del proceso.';
                    setSeedDesdeProcesoError(String(msg));
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [unidadId, location.search]);

    // En modo edición: pre-cargar datos de la operación desde location.state
    // y calcular las acciones ya asignadas a participantes existentes.
    useEffect(() => {
        if (!esEdicion) return;
        const op = location.state && location.state.operacion;
        if (!op) return;
        if (op.asunto) setAsunto(op.asunto);
        if (op.tipo_operacion) setTipoOperacion(op.tipo_operacion);
        if (op.denominacion) setDenominacion(op.denominacion);
        if (op.capital_fijo != null) setCapFijo(String(op.capital_fijo));
        if (op.capital_variable != null) setCapVariable(String(op.capital_variable));
        if (op.total_acciones != null) setTotalAcciones(String(op.total_acciones));
        if (op.tipo_representacion_capital) {
            setTipoRepresentacion(op.tipo_representacion_capital);
        }

        if (unidadId && operacionIdEdicion) {
            listOperacionSujetos(unidadId, operacionIdEdicion)
                .then((sujetos) => {
                    const todos = Array.isArray(sujetos) ? sujetos : [];

                    /**
                     * Convierte una entrada de listOperacionSujetos en un objeto
                     * compatible con NodoEntidad y adjunta recursivamente sus hijos
                     * (representante, adminUnico, subSocios) usando representa_a_sujeto_id.
                     */
                    function buildEntidad(entry) {
                        const sujetoId = entry.id_sujeto;

                        // Hijos directos: nodos cuyo padre es este sujeto
                        const hijos = todos.filter(
                            (e) => e.representa_a_sujeto_id === sujetoId
                        );

                        const esMoral =
                            (entry.tipo_persona || 'Fisica') !== 'Fisica';

                        const entidad = {
                            // Usar id_relacion (nodo único en BD) como clave, no sujetoId.
                            // El mismo sujeto puede tener múltiples nodos en una operación
                            // (p.ej. COMPRADOR + REPRESENTANTE LEGAL + ADMINISTRADOR BC);
                            // si se usa sujetoId, motorMap[id] se sobrescribe por el último
                            // nodo procesado y contamina los demás con es_bc/origen_bc incorrectos.
                            id: entry.id_relacion || sujetoId,
                            id_sujeto: sujetoId,
                            id_relacion: entry.id_relacion,
                            tipo: entry.tipo_persona || 'Fisica',
                            nombre: entry.nombre_base || '',
                            paterno: entry.paterno || '',
                            materno: entry.materno || '',
                            rfc: entry.rfc || '',
                            rolBase: entry.rol || '',
                            acciones: parseFloat(entry.acciones) || 0,
                            nacionalidad: 'México',
                            otroEspecificacion: entry.otro_especificacion || '',
                            _esExistente: true,
                        };

                        // ── Representante legal (PM) / Apoderado (PF) ────────
                        const repHijo = hijos.find(
                            (e) =>
                                e.rol.includes('REPRESENTANTE') ||
                                e.rol.includes('APODERADO')
                        );
                        if (repHijo) {
                            entidad.representante = buildEntidad(repHijo);
                            if (!esMoral) {
                                entidad.tieneRepresentante = 'si';
                            }
                        } else if (!esMoral) {
                            // Física sin apoderado registrado: dejamos '' para que
                            // el usuario vea el dropdown y pueda elegir.
                            entidad.tieneRepresentante = '';
                        }

                        // ── Estructura PLD de Moral ───────────────────────────
                        const adminHijo = hijos.find((e) =>
                            e.rol.includes('ADMINISTRADOR POR CONTROL')
                        );
                        const subSocioHijos = hijos.filter((e) =>
                            e.rol.includes('BENEFICIARIO (CASCADA)')
                        );

                        if (adminHijo) {
                            entidad.tieneSociosMayores = 'no';
                            entidad.adminUnico = buildEntidad(adminHijo);
                        } else if (subSocioHijos.length > 0) {
                            entidad.tieneSociosMayores = 'si';
                            entidad.subSocios = subSocioHijos.map(buildEntidad);
                        }
                        // else: tieneSociosMayores queda undefined → dropdown vacío

                        return entidad;
                    }

                    // Nodos raíz = sin padre
                    const raices = todos.filter((e) => !e.representa_a_sujeto_id);

                    // ── Recuperar estados de ArbolPLD desde los nodos guardados ──
                    // Los nodos "extra" (capturados vía MiniFichaPLD) no son
                    // participantes normales; se identifican por su rol y se usan
                    // para reconstruir pldOtroSocio / pldFirmante / entidadPLDExtra.
                    const PATRONES_EXTRA = [
                        'SOCIO HISTÓRICO',
                        'BENEFICIARIO CONTROLADOR',
                        'ADMINISTRADOR POR CONTROL (BC)',
                    ];
                    const esNodoExtra = (e) =>
                        PATRONES_EXTRA.some((p) => (e.rol || '').includes(p));

                    const nodoExtraEntry = raices.find(esNodoExtra);
                    const raicesNormales = raices.filter((e) => !esNodoExtra(e));

                    if (nodoExtraEntry) {
                        const extraEntidad = buildEntidad(nodoExtraEntry);
                        setEntidadPLDExtra(extraEntidad);
                        const rolExtra = nodoExtraEntry.rol || '';
                        if (
                            rolExtra.includes('SOCIO HISTÓRICO') ||
                            rolExtra.includes('BENEFICIARIO CONTROLADOR')
                        ) {
                            setPldOtroSocio('si');
                        } else if (rolExtra.includes('ADMINISTRADOR POR CONTROL (BC)')) {
                            if (op.tipo_operacion === 'CONSTITUTIVA') {
                                setVerAdminConst(true);
                            } else {
                                setPldOtroSocio('no');
                                setPldFirmante('no');
                            }
                        }
                    } else if (
                        op.tipo_operacion !== 'CONSTITUTIVA' &&
                        op.tipo_operacion !== 'RATIFICACIÓN DE CRÉDITOS'
                    ) {
                        // Sin entidad extra: los firmantes comparecen y son los BCs
                        setPldOtroSocio('no');
                        setPldFirmante('si');
                    }

                    setSujetosExistentes(raicesNormales.map(buildEntidad));
                })
                .catch(() => setSujetosExistentes([]));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [esEdicion]);

    // Wrapper que asegura el reseteo del árbol PLD al cambiar el tipo de
    // operación, para que las preguntas salvavidas no queden inconsistentes.
    function handleTipoOperacionChange(value) {
        if (esInmobiliaria) return;

        const next = typeof value === 'function' ? value(tipoOperacion) : value;
        setTipoOperacion(next);
        setEntidadPLDExtra(null);
        setPldOtroSocio('');
        setPldFirmante('');
        setVerAdminConst(false);
        setMostrarAcciones(!value.includes('RATIFICACIÓN DE CRÉDITOS'));
        setEsConstitutiva(value === 'CONSTITUTIVA');
    }

    function handleTipoRepresentacionChange(nuevoTipo) {
        setTipoRepresentacion(nuevoTipo);
        if (nuevoTipo !== 'acciones') {
            setTotalAcciones('');
        }
    }

    const rolesPermitidos = useMemo(
        () => CATALOGO_OPERACIONES[tipoOperacion] || [],
        [tipoOperacion]
    );

    const metaAcciones =
        tipoRepresentacion === 'participaciones'
            ? 100
            : parseFloat(totalAcciones || '0');
    const sumaAcciones =
        sujetosExistentes.reduce((acc, n) => acc + parseFloat(n.acciones || '0'), 0) +
        sociosRaiz.reduce((acc, s) => acc + parseFloat(s.acciones || '0'), 0);
    const porcentajeGlobal =
        metaAcciones > 0 ? ((sumaAcciones / metaAcciones) * 100).toFixed(2) : '0.00';
    const capitalCompleto = metaAcciones > 0 && sumaAcciones >= metaAcciones;
    const capitalRebasado = metaAcciones > 0 && sumaAcciones > metaAcciones;

    // Verdadero cuando ninguna persona física en la entrevista de participantes
    // supera el 25% de las acciones globales. Requiere que ya se haya definido
    // el total de acciones para poder evaluar la condición.
    const ningunaFisicaSuperaUmbral = useMemo(() => {
        if (sumaAcciones <= 0 || !capitalCompleto) return false;
        // Participantes ya guardados (editables): evaluar con el tipo ya mapeado
        const existenteSuperaUmbral = sujetosExistentes.some(
            (n) => n.tipo === 'Fisica' && (parseFloat(n.acciones || 0) / metaAcciones) * 100 > 25
        );
        if (existenteSuperaUmbral) return false;
        // Participantes nuevos en el formulario: evaluación completa
        return !sociosRaiz.some(
            (s) =>
                (s.tipo === 'Fisica' &&
                    (parseFloat(s.acciones || '0') / metaAcciones) * 100 > 25) ||
                (s.tipo === 'Moral' && s.tieneSociosMayores === 'si')
        );
    }, [sociosRaiz, metaAcciones, sumaAcciones, capitalCompleto, sujetosExistentes]);

    const muestraResumenAcciones =
        tipoOperacion === 'CONSTITUTIVA' ||
        tipoOperacion.includes('COMPRAVENTA') ||
        tipoOperacion.includes('CAPITAL') ||
        tipoOperacion.includes('FUSIÓN') ||
        tipoOperacion.includes('ESCISIÓN');

    async function procesarEntidad(
        entidad,
        operacionId,
        motorMap,
        rolForzado,
        representaAId
    ) {
        if (!entidad || !entidad.rfc || !entidad.nombre) return;

        const upsertPayload = {
            tipo_persona: entidad.tipo,
            nombre: entidad.nombre,
            paterno: entidad.paterno || null,
            materno: entidad.materno || null,
            rfc: String(entidad.rfc).toUpperCase(),
            pld_pais_nacionalidad: entidad.pldNacionalidadClave || 'MEX',
        };
        if (entidad.pldLegacyClienteId) {
            upsertPayload.legacy_id = String(entidad.pldLegacyClienteId);
        }
        const sujeto = await upsertSujetoByRfc(unidadId, upsertPayload);
        if (!sujeto || !sujeto.id) return;

        const rolFinal = rolForzado || entidad.rolBase || '';
        const motor = motorMap[entidad.id] || {
            es_bc: false,
            origen_bc: null,
        };

        await createNodo(unidadId, operacionId, {
            sujeto_id: sujeto.id,
            rol: rolFinal,
            acciones: entidad.acciones ? parseFloat(entidad.acciones) : null,
            porcentaje: entidad.porcentaje
                ? parseFloat(entidad.porcentaje)
                : null,
            representa_a_sujeto_id: representaAId || null,
            es_bc: !!motor.es_bc,
            origen_bc: motor.origen_bc || null,
            otro_especificacion: entidad.otroEspecificacion || null,
        });

        let repEntidad = null;
        let rolRep = null;
        if (entidad.tieneRepresentante === 'si' && entidad.representante) {
            repEntidad = entidad.representante;
            rolRep = 'APODERADO';
        } else if (entidad.tipo === 'Moral' && entidad.representante) {
            repEntidad = entidad.representante;
            rolRep = 'REPRESENTANTE LEGAL';
        }

        if (repEntidad) {
            await procesarEntidad(
                repEntidad,
                operacionId,
                motorMap,
                rolRep,
                sujeto.id
            );
            // Vínculo en SujetoPLD por RFC (representante_legal_id)
            if (repEntidad.rfc && repEntidad.nombre) {
                await updateSujeto(unidadId, sujeto.id, {
                    representante_legal: {
                        nombre: repEntidad.nombre,
                        paterno: repEntidad.paterno || '',
                        materno: repEntidad.materno || '',
                        rfc: String(repEntidad.rfc).toUpperCase(),
                    },
                });
            }
        }

        if (entidad.tieneSociosMayores === 'no' && entidad.adminUnico) {
            await procesarEntidad(
                entidad.adminUnico,
                operacionId,
                motorMap,
                'ADMINISTRADOR POR CONTROL (BC)',
                sujeto.id
            );
        } else if (
            entidad.tieneSociosMayores === 'si' &&
            Array.isArray(entidad.subSocios)
        ) {
            for (const sub of entidad.subSocios) {
                await procesarEntidad(
                    sub,
                    operacionId,
                    motorMap,
                    'DUEÑO BENEFICIARIO (CASCADA)',
                    sujeto.id
                );
            }
        }
    }

    async function guardarExpediente() {
        if (!esEdicion && (!asunto || !denominacion)) {
            await Swal.fire({
                icon: 'warning',
                title: 'Datos incompletos',
                text: 'Ingresa el asunto y la denominación para abrir el expediente.',
                confirmButtonColor: '#6c757d',
            });
            return;
        }
        if (esEdicion && sociosRaiz.length === 0 && sujetosExistentes.length === 0) {
            await Swal.fire({
                icon: 'warning',
                title: 'Sin participantes',
                text: 'No hay participantes que guardar.',
                confirmButtonColor: '#6c757d',
            });
            return;
        }
        const sinRfc = participantesRaizSinRfc(sociosRaiz);
        if (sinRfc.length > 0) {
            await Swal.fire({
                icon: 'warning',
                title: 'RFC incompleto',
                text: 'Cada participante principal con nombre debe tener RFC antes de guardar. Revise la entrevista de participantes.',
                confirmButtonColor: '#6c757d',
            });
            return;
        }
        if (!unidadId) {
            await Swal.fire({
                icon: 'error',
                title: 'Unidad no resuelta',
                text: 'No se pudo determinar la unidad de negocio activa.',
            });
            return;
        }

        setIsSaving(true);
        try {
            // En edición incluimos los existentes para que el motor los evalúe junto
            // con los nuevos y produzca un motorMap completo para todos los nodos.
            const raizParaMotor = esEdicion
                ? [...sujetosExistentes, ...sociosRaiz]
                : sociosRaiz;
            const planos = aplanarParticipantes(
                raizParaMotor,
                tipoRepresentacion === 'participaciones' ? 100 : totalAcciones,
                entidadPLDExtra
            );
            const motorResultados = evaluarOperacionPLD(tipoOperacion, planos);
            const motorMap = motorResultados.reduce((acc, r) => {
                acc[r.id_nodo] = {
                    es_bc: r.es_bc,
                    origen_bc: r.origen_bc,
                    perfil_sugerido: r.perfil_sugerido,
                };
                return acc;
            }, {});

            // ── Overrides de motorMap para BCs por determinación directa ─────────────
            // El motor no puede deducir es_bc para entidades sin porcentaje calculable
            // ('BENEFICIARIO CONTROLADOR' en INMOBILIARIO, 'SOCIO HISTÓRICO (>25%)').
            // Se recorre todo el árbol (raíz + extra) para fijar es_bc y origen_bc.
            function aplicarOverrideBCDirecto(entidad) {
                if (!entidad) return;
                const rol = entidad.rolBase || '';
                let origenForzado = null;
                if (rol.includes('BENEFICIARIO CONTROLADOR')) {
                    origenForzado = 'Por determinación directa';
                } else if (rol.includes('SOCIO HISTÓRICO')) {
                    origenForzado = 'Socio Histórico (>25%)';
                }
                if (origenForzado) {
                    motorMap[entidad.id] = {
                        perfil_sugerido: 'CLIENTE_DIRECTO',
                        ...(motorMap[entidad.id] || {}),
                        es_bc: true,
                        origen_bc: origenForzado,
                    };
                }
                [entidad.representante, entidad.adminUnico]
                    .concat(entidad.subSocios || [])
                    .forEach(aplicarOverrideBCDirecto);
            }
            raizParaMotor.forEach(aplicarOverrideBCDirecto);
            if (entidadPLDExtra) aplicarOverrideBCDirecto(entidadPLDExtra);

            let operacionId;

            if (esEdicion) {
                // Modo edición: actualizar estado de la operación, actualizar
                // los datos de los sujetos ya registrados y agregar los nuevos.
                await updateOperacion(unidadId, operacionIdEdicion, {
                    estado: 'En Integración',
                });
                operacionId = operacionIdEdicion;

                // Actualizar nombre/RFC y demás datos de los sujetos existentes.
                // Recorre el árbol completo (raíces + representantes, adminUnicos,
                // subSocios anidados) para actualizar cada SujetoPLD.
                function recolectarSujetos(entidad) {
                    const lista = [];
                    if (!entidad) return lista;
                    lista.push(entidad);
                    if (entidad.representante) lista.push(...recolectarSujetos(entidad.representante));
                    if (entidad.adminUnico) lista.push(...recolectarSujetos(entidad.adminUnico));
                    (entidad.subSocios || []).forEach((s) => lista.push(...recolectarSujetos(s)));
                    return lista;
                }
                const todosLosSujetos = sujetosExistentes.flatMap(recolectarSujetos);
                for (const se of todosLosSujetos) {
                    if (!se.id_sujeto) continue;
                    await updateSujeto(unidadId, se.id_sujeto, {
                        tipo_persona: se.tipo,
                        nombre: se.nombre || null,
                        paterno: se.paterno || null,
                        materno: se.materno || null,
                        rfc: se.rfc ? String(se.rfc).toUpperCase() : null,
                        pld_pais_nacionalidad: se.pldNacionalidadClave || 'MEX',
                    });
                    // Actualizar el nodo con el nuevo valor de acciones y resultado del motor
                    if (se.id_relacion) {
                        const motorSe = motorMap[se.id] || { es_bc: false, origen_bc: null };
                        await updateNodo(unidadId, se.id_relacion, {
                            acciones: se.acciones ? parseFloat(se.acciones) : null,
                            es_bc: !!motorSe.es_bc,
                            origen_bc: motorSe.origen_bc || null,
                            otro_especificacion: se.otroEspecificacion || null,
                        });
                    }
                }

                // Procesar hijos NUEVOS (sin id_sujeto) añadidos a entidades existentes.
                // Esto cubre: subSocios nuevos, adminUnico nuevo, representante nuevo.
                async function procesarHijosNuevos(entidad) {
                    if (!entidad) return;
                    const parentId = entidad.id_sujeto;
                    if (!parentId) return; // el padre mismo es nuevo; procesarEntidad lo crea completo

                    // Representante / apoderado nuevo
                    if (entidad.representante && !entidad.representante.id_sujeto) {
                        const rolRep = entidad.tipo === 'Moral'
                            ? 'REPRESENTANTE LEGAL'
                            : 'APODERADO';
                        await procesarEntidad(entidad.representante, operacionId, motorMap, rolRep, parentId);
                    }
                    // Administrador por control nuevo
                    if (entidad.adminUnico && !entidad.adminUnico.id_sujeto) {
                        await procesarEntidad(
                            entidad.adminUnico,
                            operacionId,
                            motorMap,
                            'ADMINISTRADOR POR CONTROL (BC)',
                            parentId
                        );
                    }
                    // Sub-socios en cascada nuevos
                    for (const sub of (entidad.subSocios || [])) {
                        if (!sub.id_sujeto) {
                            await procesarEntidad(
                                sub,
                                operacionId,
                                motorMap,
                                'DUEÑO BENEFICIARIO (CASCADA)',
                                parentId
                            );
                        }
                    }
                    // Recursión: buscar hijos nuevos dentro de hijos existentes
                    if (entidad.representante?.id_sujeto) await procesarHijosNuevos(entidad.representante);
                    if (entidad.adminUnico?.id_sujeto) await procesarHijosNuevos(entidad.adminUnico);
                    for (const sub of (entidad.subSocios || [])) {
                        if (sub.id_sujeto) await procesarHijosNuevos(sub);
                    }
                }
                for (const existente of sujetosExistentes) {
                    await procesarHijosNuevos(existente);
                }
            } else {
                // Modo creación: crear la operación completa
                const payloadOperacion = {
                    asunto,
                    tipo_operacion: tipoOperacion,
                    denominacion,
                    capital_fijo: capFijo || 0,
                    capital_variable: capVariable || 0,
                    total_acciones: totalAcciones || 0,
                    tipo_representacion_capital: tipoRepresentacion,
                    participaciones: [],
                    estado:
                        sociosRaiz.length === 0
                            ? 'Faltan Participantes'
                            : 'En Integración',
                };
                if (procesoIdVinculo) {
                    payloadOperacion.proceso_id = procesoIdVinculo;
                }
                const opData = await createOperacion(unidadId, payloadOperacion);
                operacionId = opData && opData.id;
                if (!operacionId) {
                    throw new Error('El servidor no devolvió el id de la operación.');
                }

                // Solicitante huérfano sólo aplica en creación
                if (solicitante.rfc && solicitante.nombre && solicitante.comparece === 'no') {
                    const solSujeto = await upsertSujetoByRfc(unidadId, {
                        tipo_persona: 'Fisica',
                        nombre: solicitante.nombre,
                        paterno: solicitante.paterno || null,
                        materno: solicitante.materno || null,
                        rfc: String(solicitante.rfc).toUpperCase(),
                    });
                    if (solSujeto && solSujeto.id) {
                        await createNodo(unidadId, operacionId, {
                            sujeto_id: solSujeto.id,
                            rol: 'SOLICITANTE / CONTACTO',
                            es_bc: false,
                            origen_bc: null,
                        });
                    }
                }
            }

            for (const socio of sociosRaiz) {
                let rolParaGuardar = socio.rolBase;
                if (
                    !esEdicion &&
                    solicitante.comparece === 'si' &&
                    socio.rfc &&
                    solicitante.rfc &&
                    socio.rfc.toUpperCase() === solicitante.rfc.toUpperCase() &&
                    socio.rolBase !== 'OTRO'   // OTRO se describe con otro_especificacion; no se prefija
                ) {
                    rolParaGuardar = `SOLICITANTE Y ${socio.rolBase}`;
                }
                await procesarEntidad(
                    socio,
                    operacionId,
                    motorMap,
                    rolParaGuardar
                );
            }

            // Procesar la entidad extra de ArbolPLD (BC / Socio Histórico / Admin BC)
            if (entidadPLDExtra && entidadPLDExtra.rfc) {
                if (entidadPLDExtra.id_relacion) {
                    // Nodo existente cargado desde BD: actualizar, nunca duplicar.
                    if (entidadPLDExtra.id_sujeto) {
                        await updateSujeto(unidadId, entidadPLDExtra.id_sujeto, {
                            tipo_persona: entidadPLDExtra.tipo,
                            nombre: entidadPLDExtra.nombre || null,
                            paterno: entidadPLDExtra.paterno || null,
                            materno: entidadPLDExtra.materno || null,
                            rfc: String(entidadPLDExtra.rfc).toUpperCase(),
                            pld_pais_nacionalidad: entidadPLDExtra.pldNacionalidadClave || 'MEX',
                        });
                    }
                    const motorExtra = motorMap[entidadPLDExtra.id] || { es_bc: false, origen_bc: null };
                    await updateNodo(unidadId, entidadPLDExtra.id_relacion, {
                        acciones: entidadPLDExtra.acciones ? parseFloat(entidadPLDExtra.acciones) : null,
                        es_bc: !!motorExtra.es_bc,
                        origen_bc: motorExtra.origen_bc || null,
                    });
                } else {
                    // Entidad nueva capturada en esta sesión: crear sujeto + nodo.
                    await procesarEntidad(entidadPLDExtra, operacionId, motorMap);
                }
            }

            await Swal.fire({
                icon: 'success',
                title: esEdicion ? 'Expediente actualizado' : 'Expediente creado',
                text: esEdicion
                    ? 'Los participantes se actualizaron y/o agregaron correctamente.'
                    : 'El expediente y sus participantes se guardaron correctamente.',
                confirmButtonColor: '#198754',
            });
            history.push(`/${unidadId}/pld/${operacionId}`);
        } catch (error) {
            const mensaje =
                (error &&
                    (error.response?.data?.error ||
                        error.message ||
                        String(error))) ||
                'Error desconocido';
            await Swal.fire({
                icon: 'error',
                title: 'Error al guardar',
                text: mensaje,
            });
        } finally {
            setIsSaving(false);
        }
    }

    return (
        <Container>
            <Header>
                {esEdicion
                    ? `Agregar participantes${denominacion ? ` — ${denominacion}` : ''}`
                    : 'Nuevo expediente PLD'}
            </Header>
            {esEdicion ? (
                <Alert color="info" className="mt-2">
                    Estás agregando participantes a un expediente existente. Los
                    datos del expediente (asunto, sociedad, capital) no se
                    modificarán.
                </Alert>
            ) : null}
            {!esEdicion && procesoIdVinculo ? (
                <Alert color="info" className="mt-2">
                    Expediente vinculado al proceso{' '}
                    <strong>{procesoIdVinculo}</strong>. Los datos se
                    prellenaron desde la información preliminar; revise RFC y
                    roles antes de guardar.
                </Alert>
            ) : null}
            {!esEdicion && seedDesdeProcesoError ? (
                <Alert color="warning" className="mt-2">
                    {seedDesdeProcesoError}
                </Alert>
            ) : null}

            {!esEdicion && !esInmobiliaria ? (
                <FormExpediente
                    esInmobiliaria={esInmobiliaria}
                    unidadId={unidadId}
                    asunto={asunto}
                    tipoOperacion={tipoOperacion}
                    solicitante={solicitante}
                    sociosRaiz={sociosRaiz}
                    setAsunto={setAsunto}
                    setTipoOperacion={handleTipoOperacionChange}
                    setSolicitante={setSolicitante}
                    setSociosRaiz={setSociosRaiz}
                />
            ) : null}

            {!esEdicion && esInmobiliaria ? (
                <FormExpedienteInmuebles
                    unidadId={unidadId}
                    asunto={asunto}
                    tipoOperacion={tipoOperacion}
                    solicitante={solicitante}
                    sociosRaiz={sociosRaiz}
                    setAsunto={setAsunto}
                    setTipoOperacion={handleTipoOperacionChange}
                    setSolicitante={setSolicitante}
                    setSociosRaiz={setSociosRaiz}
                />
            ) : null}

            {!esEdicion && !esInmobiliaria && (
                <FormSociedad
                    mostrarAcciones={mostrarAcciones}
                    esConstitutiva={esConstitutiva}
                    denominacion={denominacion}
                    capFijo={capFijo}
                    capVariable={capVariable}
                    totalAcciones={mostrarAcciones ? totalAcciones : 0}
                    onDenominacionChange={(e) => setDenominacion(e.target.value)}
                    onCapFijoChange={(e) => setCapFijo(e.target.value)}
                    onCapVariableChange={(e) => setCapVariable(e.target.value)}
                    onTotalAccionesChange={(e) => setTotalAcciones(e.target.value)}
                    tipoRepresentacion={tipoRepresentacion}
                    onTipoRepresentacionChange={handleTipoRepresentacionChange}
                />
            )}

            <Card className="mb-4 shadow-sm">
                <CardHeader className="border-bottom border-secondary">
                    <Row className="align-items-center">
                        <Col>
                            <h5 className="mb-0 d-flex align-items-center">
                                <FontAwesomeIcon
                                    icon={faUsers}
                                    className="text-secondary mr-2"
                                />
                                Entrevista de clientes
                            </h5>
                        </Col>
                        {muestraResumenAcciones && !esInmobiliaria ? (
                            <Col xs="auto">
                                <Badge
                                    color={
                                        capitalCompleto && !capitalRebasado
                                            ? 'success'
                                            : 'info'
                                    }
                                    pill
                                    className="px-3 py-2 d-inline-flex align-items-center"
                                >
                                    {tipoRepresentacion === 'participaciones' ? (
                                        <>
                                            {capitalCompleto ? (
                                                <FontAwesomeIcon
                                                    icon={faShieldAlt}
                                                    className="mr-2"
                                                />
                                            ) : null}
                                            <span className="font-weight-bold">
                                                {sumaAcciones.toFixed(2)}% asignado
                                            </span>
                                        </>
                                    ) : (
                                        <>
                                            {capitalCompleto ? (
                                                <FontAwesomeIcon
                                                    icon={faShieldAlt}
                                                    className="mr-2"
                                                />
                                            ) : null}
                                            <span className="font-weight-bold">
                                                {sumaAcciones} / {metaAcciones || '?'}{' '}
                                                ({porcentajeGlobal}%)
                                            </span>
                                        </>
                                    )}
                                </Badge>
                            </Col>
                        ) : null}
                    </Row>
                </CardHeader>
                <CardBody>
                    {sujetosExistentes.length === 0 && sociosRaiz.length === 0 ? (
                        <p className="text-muted small mb-3">
                            Aún no se han agregado participantes.
                        </p>
                    ) : null}

                    {/* — Participantes ya registrados (editables en modo edición) — */}
                    {esEdicion && sujetosExistentes.length > 0 ? (
                        <>
                            <p className="small font-weight-bold text-uppercase text-muted mb-2">
                                Participantes registrados
                            </p>
                            {sujetosExistentes.map((sujeto, index) => (
                                <NodoEntidad
                                    key={sujeto.id}
                                    entidad={sujeto}
                                    nivel={0}
                                    numeroSocio={index + 1}
                                    totalAccionesGlobal={
                                        tipoRepresentacion === 'participaciones'
                                            ? 100
                                            : totalAcciones
                                    }
                                    porcentajeOcupadoOtros={
                                        tipoRepresentacion === 'participaciones'
                                            ? sujetosExistentes
                                                  .filter((s) => s.id !== sujeto.id)
                                                  .reduce(
                                                      (acc, s) =>
                                                          acc + parseFloat(s.acciones || '0'),
                                                      0
                                                  ) +
                                              sociosRaiz.reduce(
                                                  (acc, s) =>
                                                      acc + parseFloat(s.acciones || '0'),
                                                  0
                                              )
                                            : 0
                                    }
                                    paises={paises}
                                    rolesPermitidos={rolesPermitidos}
                                    mostrarAccionesProp={mostrarAcciones}
                                    tipoRepresentacionGlobal={esInmobiliaria ? 'sin-acciones' : tipoRepresentacion}
                                    actualizar={(mod) =>
                                        setSujetosExistentes(
                                            sujetosExistentes.map((s) =>
                                                s.id === mod.id ? mod : s
                                            )
                                        )
                                    }
                                    // Sin prop 'eliminar' → no muestra botón Quitar
                                />
                            ))}
                            {sociosRaiz.length > 0 ? (
                                <hr className="my-3" />
                            ) : null}
                        </>
                    ) : null}

                    {/* — Nuevos participantes — */}
                    {esEdicion && sociosRaiz.length > 0 ? (
                        <p className="small font-weight-bold text-uppercase text-muted mb-2">
                            Nuevos participantes
                        </p>
                    ) : null}
                    { !esInmobiliaria && sociosRaiz.map((socio, index) => (
                        <NodoEntidad
                            key={socio.id}
                            entidad={socio}
                            nivel={0}
                            numeroSocio={index + 1}
                            totalAccionesGlobal={
                                tipoRepresentacion === 'participaciones'
                                    ? 100
                                    : totalAcciones
                            }
                            porcentajeOcupadoOtros={
                                tipoRepresentacion === 'participaciones'
                                    ? sujetosExistentes.reduce(
                                          (acc, n) =>
                                              acc + parseFloat(n.acciones || '0'),
                                          0
                                      ) +
                                      sociosRaiz
                                          .filter((s) => s.id !== socio.id)
                                          .reduce(
                                              (acc, s) =>
                                                  acc +
                                                  parseFloat(s.acciones || '0'),
                                              0
                                          )
                                    : 0
                            }
                            paises={paises}
                            rolesPermitidos={rolesPermitidos}
                            mostrarAccionesProp={mostrarAcciones}
                            tipoRepresentacionGlobal={tipoRepresentacion}
                            actualizar={(mod) =>
                                setSociosRaiz(
                                    sociosRaiz.map((s) =>
                                        s.id === mod.id ? mod : s
                                    )
                                )
                            }
                            eliminar={() =>
                                setSociosRaiz(
                                    sociosRaiz.filter((s) => s.id !== socio.id)
                                )
                            }
                        />
                    ))}

                    

                    {esInmobiliaria && sociosRaiz.map((socio, index) => (
                        <NodoEntidad
                            key={socio.id}
                            entidad={socio}
                            nivel={0}
                            numeroSocio={index + 1}
                            totalAccionesGlobal={0}
                            porcentajeOcupadoOtros={0}
                            paises={paises}
                            rolesPermitidos={rolesPermitidos}
                            mostrarAccionesProp={false}
                            tipoRepresentacionGlobal="sin-acciones"
                            actualizar={(mod) =>
                                setSociosRaiz(sociosRaiz.map((s) => s.id === mod.id ? mod : s))
                            }
                            eliminar={() => setSociosRaiz(sociosRaiz.filter((s) => s.id !== socio.id))}
                        />
                    ))}

                    <div className="pt-3 border-top">
                        <Button
                            type="button"
                            color="primary"
                            disabled={capitalCompleto && metaAcciones > 0}
                            onClick={() =>
                                setSociosRaiz([...sociosRaiz, nuevoSocio()])
                            }
                        >
                            <FontAwesomeIcon icon={faPlus} className="mr-1" />
                            {capitalCompleto && metaAcciones > 0
                                ? `${capitalRebasado ? 'Capital rebasado' : 'Capital al 100%'}`
                                : 'Agregar Cliente'}
                        </Button>
                    </div>
                </CardBody>
            </Card>

            <ArbolPLD
                tipoOperacion={tipoOperacion}
                ningunaFisicaSuperaUmbral={ningunaFisicaSuperaUmbral}
                verAdminConst={verAdminConst}
                setVerAdminConst={setVerAdminConst}
                pldOtroSocio={pldOtroSocio}
                setPldOtroSocio={setPldOtroSocio}
                pldFirmante={pldFirmante}
                setPldFirmante={setPldFirmante}
                entidadPLDExtra={entidadPLDExtra}
                setEntidadPLDExtra={setEntidadPLDExtra}
                denominacion={denominacion}
                esInmobiliaria={esInmobiliaria}
            />

            <Row className="justify-content-end pb-5 pt-2">
                <Col xs="auto">
                    <Button
                        type="button"
                        color="success"
                        size="lg"
                        className="font-weight-bold px-4"
                        onClick={guardarExpediente}
                        disabled={isSaving}
                    >
                        <FontAwesomeIcon
                            icon={isSaving ? faSpinner : faCheckCircle}
                            spin={isSaving}
                            className="mr-2"
                        />
                        {isSaving ? 'GUARDANDO...' : esEdicion ? 'AGREGAR PARTICIPANTES' : 'CREAR EXPEDIENTE'}
                    </Button>
                </Col>
            </Row>
        </Container>
    );
}
